import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2, HelpCircle, UploadCloud } from "lucide-react";
import type { ProviderLimit } from "../content/limits";
import { PROVIDER_LIMITS } from "../content/limits";
import { ContextualLinks } from "../components/ContextualLinks";
import { useUi } from "../i18n";

export function LimitsHub({ item }: { item: ProviderLimit }) {
  const { t } = useUi();
  const others = PROVIDER_LIMITS.filter((p) => p.slug !== item.slug);

  return (
    <article className="panel legal" style={{ maxWidth: 780, margin: "0 auto" }}>
      <header style={{ marginBottom: 24 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <AlertCircle size={12} /> {item.provider} Attachment Guide (2026)
        </span>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          {item.h1}
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          {item.summary}
        </p>
      </header>

      {/* Quick Action Box (Theme-Adaptive) */}
      <div style={{ padding: 24, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 32, textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8, color: "var(--lime)" }}>
          <UploadCloud size={24} />
          <b style={{ fontSize: 16 }}>{t.ctaReadyTitle} ({item.provider})</b>
        </div>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          {t.ctaReadySub}
        </p>
        <Link to="/" className="btn block" style={{ maxWidth: 360, margin: "0 auto", textDecoration: "none" }}>
          <span>{t.ctaStartBtn}</span> <ArrowRight size={16} />
        </Link>
      </div>

      <section style={{ marginBottom: 28 }}>
        <h2>{item.provider} limit breakdown</h2>
        <table>
          <thead>
            <tr>
              <th>Constraint</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 600 }}>Stated attachment limit</td>
              <td>{item.details.maxAttachment}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Encoding overhead</td>
              <td>{item.details.encodingOverhead}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Cloud fallback behavior</td>
              <td>{item.details.cloudFallback}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Recipient requirement</td>
              <td>{item.details.recipientRequirement}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Privacy &amp; scanning</td>
              <td>{item.details.securityLevel}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2>Why files bounce on {item.provider}</h2>
        <p>{item.whyItBounces}</p>
      </section>

      <section style={{ marginBottom: 28 }}>
        <h2>How to send files larger than {item.officialLimit}</h2>
        <ul style={{ display: "flex", flexDirection: "column", gap: 10, paddingLeft: 0, listStyle: "none" }}>
          {item.howToBypass.map((step, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <CheckCircle2 size={16} color="#00ff66" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>Frequently asked questions</h2>
        {item.faq.map((f, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 4px", fontSize: 15 }}>
              <HelpCircle size={15} color="var(--lime)" /> {f.q}
            </h3>
            <p className="hint" style={{ margin: 0 }}>{f.a}</p>
          </div>
        ))}
      </section>

      <ContextualLinks currentSlug={item.slug} />
    </article>
  );
}
